import React, { useState, useEffect } from 'react';
import { AlertTriangle, Camera, Clock, RefreshCw, ChevronLeft, ChevronRight, Eye, Calendar, Shield, Activity, Bell, Search, Filter, X, Users, Scan } from 'lucide-react';

const CCTVDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [stats, setStats] = useState({ total: 0, today: 0, thisWeek: 0 });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(1);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [searchDevice, setSearchDevice] = useState('');
  const [activeDeviceFilter, setActiveDeviceFilter] = useState(null);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [boundingBoxModal, setBoundingBoxModal] = useState(null);
  const [boundingBoxData, setBoundingBoxData] = useState(null);
  const [loadingBoundingBox, setLoadingBoundingBox] = useState(false);
  const [showEventLog, setShowEventLog] = useState(false);
  const [eventLogs, setEventLogs] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventError, setEventError] = useState('');
  const [eventCurrentPage, setEventCurrentPage] = useState(1);
  const [eventTotalPages, setEventTotalPages] = useState(1);
  const [eventTotalItems, setEventTotalItems] = useState(0);
  const [eventItemsPerPage] = useState(20);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    device_sn: '',
    name: '',
    start_time: '',
    end_time: '',
    days: [],
    motion_detection: true,
    person_recognition: true,
    is_active: true
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null);
  const [showDetectedOnly, setShowDetectedOnly] = useState(false);
  const [detectedStats, setDetectedStats] = useState({ total: 0, today: 0, thisWeek: 0 });

  // State for the new Authorized Person Search feature
  const [showAuthorizedSearchModal, setShowAuthorizedSearchModal] = useState(false);
  const [authorizedSearchResults, setAuthorizedSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(1); // To track which page we are searching on the backend

  // State for the new user menu dropdown
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  //Face Registry State
  const [showFaceRegistryModal, setShowFaceRegistryModal] = useState(false);
  const [registeredPersons, setRegisteredPersons] = useState([]);
  const [loadingRegistry, setLoadingRegistry] = useState(false);

  // Hardcoded data for the face registry
  const hardcodedRegisteredPersons = {
    "persons": [
      { "image_count": 3, "person_id": "Anton" },
      { "image_count": 6, "person_id": "Kevin" },
      { "image_count": 3, "person_id": "Thomas" },
      { "image_count": 5, "person_id": "Arba" },
      { "image_count": 3, "person_id": "Bagas" },
      { "image_count": 6, "person_id": "Stillmen" },
      { "image_count": 3, "person_id": "Rizky_Rama" },
      { "image_count": 3, "person_id": "Fajar" },
      { "image_count": 3, "person_id": "Archive" }
    ],
    "status": "success"
  };

  // Effect to close the menu when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const menuContainer = document.getElementById('user-menu-container');
      if (menuContainer && !menuContainer.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // This state will help us reopen the search modal after closing the details
  const [reopenSearchOnClose, setReopenSearchOnClose] = useState(false);

  // Auth token constant - you can change this
  const AUTH_TOKEN = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJmcmVzaCI6ZmFsc2UsImlhdCI6MTc1MTM0MDMyNSwianRpIjoiZWQzMDQ3NjQtODFjMi00N2ZlLTgwN2QtMGYzOWFhZjUyNzk1IiwidHlwZSI6ImFjY2VzcyIsInN1YiI6MSwibmJmIjoxNzUxMzQwMzI1LCJjc3JmIjoiZWQwNDM2MjctNWRiOC00NjY3LTg0NGUtYmRjMjBmMjIxNDc4IiwiZXhwIjoxNzgyODc2MzI1fQ.UH-P6d8Hc0cyLIZs3LMOJ_-mqZDVDzGINyzLmNANEYg";

  // Sample schedule data - replace with real API later
  const sampleSchedules = [
    {
      id: 1,
      device_sn: 'CAM001',
      device_name: 'Front Door Camera',
      name: 'Business Hours',
      start_time: '08:00',
      end_time: '18:00',
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      motion_detection: true,
      person_recognition: true,
      is_active: true,
      created_at: '2024-01-15T08:00:00Z'
    },
    {
      id: 2,
      device_sn: 'CAM002',
      device_name: 'Parking Lot Camera',
      name: 'Night Security',
      start_time: '22:00',
      end_time: '06:00',
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
      motion_detection: true,
      person_recognition: false,
      is_active: true,
      created_at: '2024-01-15T10:30:00Z'
    },
    {
      id: 3,
      device_sn: 'CAM001',
      device_name: 'Front Door Camera',
      name: 'Weekend Monitoring',
      start_time: '09:00',
      end_time: '17:00',
      days: ['saturday', 'sunday'],
      motion_detection: true,
      person_recognition: true,
      is_active: false,
      created_at: '2024-01-16T14:20:00Z'
    }
  ];

  // Load cached token on component mount
  useEffect(() => {
    const cachedToken = getCachedToken();
    if (cachedToken) {
      setIsTokenValid(true);
      fetchAlerts(1, cachedToken);
      fetchDevices(cachedToken);
    }
  }, []);

  // Auto-refresh effect
  useEffect(() => {
    let intervalId;
    if (autoRefresh && isTokenValid) {
      intervalId = setInterval(() => {
        if (activeDeviceFilter) {
          if (showDetectedOnly) {
            fetchDeviceDetectedAlerts(activeDeviceFilter, currentPage, AUTH_TOKEN);
          } else {
            fetchDeviceAlerts(activeDeviceFilter, currentPage, AUTH_TOKEN);
          }
        } else {
          if (showDetectedOnly) {
            fetchDetectedAlerts(currentPage, AUTH_TOKEN);
          } else {
            fetchAlerts(currentPage, AUTH_TOKEN);
          }
        }
        setLastRefresh(new Date());
      }, refreshInterval * 60 * 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, isTokenValid, currentPage, refreshInterval, activeDeviceFilter, showDetectedOnly]);

  // This effect will trigger a data refetch whenever the page or filters change.
  useEffect(() => {
    // Don't fetch if the user isn't logged in yet
    if (!isTokenValid) return;

    // Determine which data to fetch based on active filters
    if (activeDeviceFilter) {
      if (showDetectedOnly) {
        fetchDeviceDetectedAlerts(activeDeviceFilter, currentPage);
      } else {
        fetchDeviceAlerts(activeDeviceFilter, currentPage);
      }
    } else {
      if (showDetectedOnly) {
        fetchDetectedAlerts(currentPage);
      } else {
        fetchAlerts(currentPage);
      }
    }
  }, [currentPage, showDetectedOnly, activeDeviceFilter, isTokenValid]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.getElementById('search-bar-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowSearchBar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cacheToken = (token) => {
    const expiryTime = new Date().getTime() + (30 * 60 * 1000);
    localStorage.setItem('cctv_token', JSON.stringify({
      token: token,
      expiry: expiryTime
    }));
  };

  const getCachedToken = () => {
    try {
      const cached = localStorage.getItem('cctv_token');
      if (cached) {
        const { token, expiry } = JSON.parse(cached);
        if (new Date().getTime() < expiry) {
          return token;
        } else {
          localStorage.removeItem('cctv_token');
        }
      }
    } catch (error) {
      localStorage.removeItem('cctv_token');
    }
    return null;
  };

  const fetchDevices = async (token = AUTH_TOKEN) => {
    try {
      const response = await fetch('https://vl-be.ayenz.my.id/api/devices', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const devices = data.result || [];
      
      // Ensure we have an array of device objects
      if (Array.isArray(devices)) {
        setAvailableDevices(devices);
      } else {
        console.error('Devices data is not an array:', devices);
        setAvailableDevices([]);
      }
      
    } catch (err) {
      console.error('Failed to fetch devices:', err.message);
      setAvailableDevices([]);
    }
  };

  const fetchAlerts = async (page = 1, token = AUTH_TOKEN) => {
    setLoading(true);
    setError('');
    try {
      const offset = (page - 1) * itemsPerPage;
      const response = await fetch(`https://vl-be.ayenz.my.id/api/alerts?limit=${itemsPerPage}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const alerts = data.result || [];
      const pagination = data.pagination || {};
      
      setAlerts(alerts);
      setTotalItems(pagination.total || 0);
      setTotalPages(Math.ceil((pagination.total || 0) / itemsPerPage));
      setCurrentPage(page);
      
      // For stats, we need to fetch all alerts or use the total from pagination
      calculateStatsFromPagination(pagination.total || 0);
      
      if (!isTokenValid) {
        cacheToken(token);
        setIsTokenValid(true);
        fetchDevices(token);
      }
      
      setLastRefresh(new Date());
      
    } catch (err) {
      setError(`Failed to fetch alerts: ${err.message}`);
      if (err.message.includes('401') || err.message.includes('403')) {
        localStorage.removeItem('cctv_token');
        setIsTokenValid(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // This function will fetch registered persons. It's using hardcoded data for now.
  const fetchRegisteredPersons = async () => {
    setLoadingRegistry(true);
    
    // --- FUTURE API IMPLEMENTATION ---
    // To switch to a real API, uncomment the block below and remove the hardcoded part.
    /*
    try {
        const response = await fetch('YOUR_API_ENDPOINT_HERE/persons', {
            headers: { 'Authorization': `Bearer ${AUTH_TOKEN}` }
        });
        if (!response.ok) {
            throw new Error('Failed to fetch registered persons');
        }
        const data = await response.json();
        setRegisteredPersons(data.persons || []);
    } catch (error) {
        console.error("Error fetching registered persons:", error);
        // Optionally set an error state here
    } finally {
        setLoadingRegistry(false);
    }
    */

    // --- HARDCODED IMPLEMENTATION (FOR NOW) ---
    console.log("Fetching registered persons (using hardcoded data)...");
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
    setRegisteredPersons(hardcodedRegisteredPersons.persons);
    setLoadingRegistry(false);
  };

  const fetchDetectedAlerts = async (page = 1, token = AUTH_TOKEN) => {
  setLoading(true);
  setError('');
  try {
    const offset = (page - 1) * itemsPerPage;
    const response = await fetch(`https://vl-be.ayenz.my.id/api/alerts/detected?limit=${itemsPerPage}&offset=${offset}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    const alerts = data.result || [];
    const pagination = data.pagination || {};
    
    setAlerts(alerts);
    setTotalItems(pagination.total || 0);
    setTotalPages(Math.ceil((pagination.total || 0) / itemsPerPage));
    setCurrentPage(page);
    
    // Calculate stats for detected alerts only
    calculateDetectedStatsFromPagination(pagination.total || 0);
    setLastRefresh(new Date());
    
  } catch (err) {
    setError(`Failed to fetch detected alerts: ${err.message}`);
    if (err.message.includes('401') || err.message.includes('403')) {
      localStorage.removeItem('cctv_token');
      setIsTokenValid(false);
    }
  } finally {
    setLoading(false);
  }
};

  const fetchDeviceDetectedAlerts = async (deviceName, page = 1, token = AUTH_TOKEN) => {
    setLoading(true);
    setError('');
    try {
      const offset = (page - 1) * itemsPerPage;
      // Note: This endpoint may not exist yet, you might need to confirm with backend
      const response = await fetch(`https://vl-be.ayenz.my.id/api/alerts/detected?device_sn=${encodeURIComponent(deviceName)}&limit=${itemsPerPage}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const alerts = data.result || [];
      const pagination = data.pagination || {};

      setAlerts(alerts);
      setTotalItems(pagination.total || 0);
      setTotalPages(Math.ceil((pagination.total || 0) / itemsPerPage));
      setCurrentPage(page);

      calculateDetectedStatsFromPagination(pagination.total || 0);
      setLastRefresh(new Date());

    } catch (err) {
      setError(`Failed to fetch detected alerts for device ${deviceName}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateDetectedStatsFromPagination = (total) => {
    const estimatedToday = Math.floor(total * 0.1);
    const estimatedWeek = Math.floor(total * 0.3);

    setDetectedStats({
      total: total,
      today: estimatedToday,
      thisWeek: estimatedWeek
    });
  };

  const fetchDeviceAlerts = async (deviceName, page = 1, token = AUTH_TOKEN) => {
    setLoading(true);
    setError('');
    try {
      const offset = (page - 1) * itemsPerPage;
      const response = await fetch(`https://vl-be.ayenz.my.id/api/alerts/device/${encodeURIComponent(deviceName)}?limit=${itemsPerPage}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const alerts = data.result || [];
      const pagination = data.pagination || {};
      
      setAlerts(alerts);
      setTotalItems(pagination.total || 0);
      setTotalPages(Math.ceil((pagination.total || 0) / itemsPerPage));
      setCurrentPage(page);
      
      calculateStatsFromPagination(pagination.total || 0);
      setLastRefresh(new Date());
      
    } catch (err) {
      setError(`Failed to fetch alerts for device ${deviceName}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoundingBoxData = async (alert) => {
    setLoadingBoundingBox(true);
    setBoundingBoxData(null);

    try {
      const response = await fetch(`https://vl-be.ayenz.my.id/api/alerts/${alert.id}/detections`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AUTH_TOKEN}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Transform the API response to match the existing component structure
      const transformedData = {
        status: "SUCCESS",
        results: data.result.map(detection => ({
          authorized: detection.is_authorized === 1,
          bounding_box: detection.bounding_box,
          confidence: detection.confidence,
          person_id: detection.person_id
        }))
      };

      setBoundingBoxData(transformedData);
    } catch (err) {
      setError(`Failed to fetch detection data: ${err.message}`);
    } finally {
      setLoadingBoundingBox(false);
    }
  };

  const handleShowBoundingBoxes = (alert) => {
    setBoundingBoxModal(alert);
    fetchBoundingBoxData(alert);
  };

  const handleShowPreloadedDetections = (alertData) => {
    // The Person ID modal expects an 'alert' object in its state to display the header.
    // We create an object that matches the expected structure, mapping 'alert_id' to 'id'.
    const alertForModal = { ...alertData, id: alertData.alert_id };
    setBoundingBoxModal(alertForModal);

    // The modal also expects a 'boundingBoxData' object. We can create this directly
    // from the 'detections' array that came with our search result. No extra API call needed!
    const preloadedBoundingBoxData = {
      status: "SUCCESS",
      results: alertData.detections.map(detection => ({
        authorized: detection.is_authorized === 1,
        bounding_box: detection.bounding_box,
        confidence: detection.confidence,
        person_id: detection.person_id
      }))
    };
    setBoundingBoxData(preloadedBoundingBoxData);
    setLoadingBoundingBox(false); // Ensure loading is off
  };

  const calculateStatsFromPagination = (total) => {
    // Since we're using server-side pagination, we can't calculate exact today/week stats
    // without additional API calls. For now, we'll estimate based on total
    const estimatedToday = Math.floor(total * 0.1); // Assume 10% are from today
    const estimatedWeek = Math.floor(total * 0.3); // Assume 30% are from this week

    setStats({
      total: total,
      today: estimatedToday,
      thisWeek: estimatedWeek
    });
  };

  const handleLogin = () => {
    if (username === 'admin' && password === 'admin') {
      setIsTokenValid(true);
      setActiveDeviceFilter(null);
      fetchAlerts(1, AUTH_TOKEN);
      fetchDevices(AUTH_TOKEN);
    } else {
      setError('Invalid username or password');
    }
  };

  const handleDeviceSearch = () => {
    if (searchDevice.trim()) {
      setActiveDeviceFilter(searchDevice.trim());
      setCurrentPage(1); // Reset to first page when searching
      fetchDeviceAlerts(searchDevice.trim(), 1);
      setShowSearchBar(false);
    }
  };

  const handleShowAllDevices = () => {
    setActiveDeviceFilter(null);
    setSearchDevice('');
    setCurrentPage(1); // Reset to first page
    fetchAlerts(1);
    setShowSearchBar(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('cctv_token');
    setUsername('');
    setPassword('');
    setIsTokenValid(false);
    setAlerts([]);
    setActiveDeviceFilter(null);
    setSearchDevice('');
    setShowSearchBar(false);
    setStats({ total: 0, today: 0, thisWeek: 0 });
    setViewMode('cards');
    setCurrentPage(1);
    setTotalPages(1);
    setTotalItems(0);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getImageUrl = (imagePath) => {
    return `https://vl-be.ayenz.my.id/images/${imagePath}`;
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - alertTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getPaginationRange = () => {
    const range = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        range.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisible - 1);
      
      for (let i = start; i <= end; i++) {
        range.push(i);
      }
    }
    
    return range;
  };

  const fetchEventLogs = async (page = 1, token = AUTH_TOKEN) => {
    setLoadingEvents(true);
    setEventError('');
    try {
      const offset = (page - 1) * eventItemsPerPage;
      const response = await fetch(`https://vl-be.ayenz.my.id/api/status/events?limit=${eventItemsPerPage}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const events = data.result || [];
      const pagination = data.pagination || {};

      setEventLogs(events);
      setEventTotalItems(pagination.total || 0);
      setEventTotalPages(Math.ceil((pagination.total || 0) / eventItemsPerPage));
      setEventCurrentPage(page);

    } catch (err) {
      setEventError(`Failed to fetch event logs: ${err.message}`);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleEventPageChange = (page) => {
    if (page >= 1 && page <= eventTotalPages && page !== eventCurrentPage) {
      fetchEventLogs(page);
    }
  };

  const getEventPaginationRange = () => {
    const range = [];
    const maxVisible = 5;

    if (eventTotalPages <= maxVisible) {
      for (let i = 1; i <= eventTotalPages; i++) {
        range.push(i);
      }
    } else {
      const start = Math.max(1, eventCurrentPage - 2);
      const end = Math.min(eventTotalPages, start + maxVisible - 1);

      for (let i = start; i <= end; i++) {
        range.push(i);
      }
    }

    return range;
  };

  const getEventTypeColor = (eventType) => {
    switch (eventType.toLowerCase()) {
      case 'initialized':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'disconnected':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const BoundingBoxOverlay = ({ boundingBoxData, imageElement }) => {
    if (!boundingBoxData || !imageElement) return null;

    const imageRect = imageElement.getBoundingClientRect();
    const containerRect = imageElement.parentElement.getBoundingClientRect();

    return (
      <div className="absolute inset-0 pointer-events-none">
        {boundingBoxData.results.map((result, index) => {
          const { x1, y1, x2, y2 } = result.bounding_box;
          
          // Calculate relative position and size
          const left = (x1 / imageElement.naturalWidth) * 100;
          const top = (y1 / imageElement.naturalHeight) * 100;
          const width = ((x2 - x1) / imageElement.naturalWidth) * 100;
          const height = ((y2 - y1) / imageElement.naturalHeight) * 100;

          const isAuthorized = result.authorized;
          const personId = result.person_id;
          const confidence = result.confidence;

          return (
            <div
              key={index}
              className="absolute"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${width}%`,
                height: `${height}%`,
              }}
            >
              {/* Bounding Box */}
              <div
                className={`w-full h-full border-2 ${
                  isAuthorized ? 'border-green-500' : 'border-red-500'
                } bg-transparent`}
              >
                {/* Label */}
                <div
                  className={`absolute -top-8 left-0 px-2 py-1 text-xs font-semibold rounded ${
                    isAuthorized 
                      ? 'bg-green-500 text-white' 
                      : 'bg-red-500 text-white'
                  }`}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {personId === 'unknown' ? 'Unknown' : personId}
                  {confidence > 0 && (
                    <span className="ml-1 opacity-80">
                      ({Math.round(confidence * 100)}%)
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const fetchSchedules = async () => {
    setLoadingSchedules(true);
    setScheduleError('');
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));

      // For now, use sample data
      setSchedules(sampleSchedules);
    } catch (err) {
      setScheduleError(`Failed to fetch schedules: ${err.message}`);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSchedules(true);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (editingSchedule) {
        // Update existing schedule
        const updatedSchedules = schedules.map(schedule =>
          schedule.id === editingSchedule.id
            ? { ...scheduleForm, id: editingSchedule.id, created_at: editingSchedule.created_at }
            : schedule
        );
        setSchedules(updatedSchedules);
      } else {
        // Add new schedule
        const newSchedule = {
          ...scheduleForm,
          id: Math.max(...schedules.map(s => s.id), 0) + 1,
          device_name: availableDevices.find(d => d.device_sn === scheduleForm.device_sn)?.device_name || 'Unknown Device',
          created_at: new Date().toISOString()
        };
        setSchedules([...schedules, newSchedule]);
      }

      // Reset form and close modal
      setScheduleForm({
        device_sn: '',
        name: '',
        start_time: '',
        end_time: '',
        days: [],
        motion_detection: true,
        person_recognition: true,
        is_active: true
      });
      setEditingSchedule(null);
      setShowScheduleModal(false);
    } catch (err) {
      setScheduleError(`Failed to save schedule: ${err.message}`);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleEditSchedule = (schedule) => {
    setScheduleForm({
      device_sn: schedule.device_sn,
      name: schedule.name,
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      days: schedule.days,
      motion_detection: schedule.motion_detection,
      person_recognition: schedule.person_recognition,
      is_active: schedule.is_active
    });
    setEditingSchedule(schedule);
    setShowScheduleModal(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    setLoadingSchedules(true);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      setSchedules(schedules.filter(s => s.id !== scheduleId));
      setDeleteConfirmModal(null);
    } catch (err) {
      setScheduleError(`Failed to delete schedule: ${err.message}`);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleToggleSchedule = async (scheduleId, isActive) => {
    setLoadingSchedules(true);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      const updatedSchedules = schedules.map(schedule =>
        schedule.id === scheduleId
          ? { ...schedule, is_active: isActive }
          : schedule
      );
      setSchedules(updatedSchedules);
    } catch (err) {
      setScheduleError(`Failed to update schedule: ${err.message}`);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const formatDays = (days) => {
    const dayNames = {
      monday: 'Mon',
      tuesday: 'Tue',
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat',
      sunday: 'Sun'
    };

    if (days.length === 7) return 'Every day';
    if (days.length === 5 && !days.includes('saturday') && !days.includes('sunday')) return 'Weekdays';
    if (days.length === 2 && days.includes('saturday') && days.includes('sunday')) return 'Weekends';

    return days.map(day => dayNames[day]).join(', ');
  };

  const getCurrentActiveSchedules = () => {
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.toTimeString().slice(0, 5);

    return schedules.filter(schedule => {
      if (!schedule.is_active) return false;
      if (!schedule.days.includes(currentDay)) return false;

      // Handle overnight schedules (e.g., 22:00 to 06:00)
      if (schedule.start_time > schedule.end_time) {
        return currentTime >= schedule.start_time || currentTime <= schedule.end_time;
      } else {
        return currentTime >= schedule.start_time && currentTime <= schedule.end_time;
      }
    });
  };

  // New, efficient function to fetch authorized alerts from the dedicated API
  const fetchAuthorizedAlerts = async (page = 1) => {
    setLoading(true); // Or a new loading state for the modal, e.g., setLoadingModal(true)
    setError('');

    try {
      // Using 'limit' and 'offset' as it's the same structure as /api/alerts
      const limit = itemsPerPage;
      const offset = (page - 1) * limit;
      
      const response = await fetch(`https://vl-be.ayenz.my.id/api/detections/authorized?limit=${limit}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${AUTH_TOKEN}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      const searchResults = data.result || [];
      const paginationInfo = data.pagination || {};

      // This assumes you want to display the results in a new search modal.
      setAuthorizedSearchResults(searchResults); 
      
      // Update pagination state for the search modal (if it has its own pagination)
      if (paginationInfo) {
        setSearchPage(page);
        // You can set total pages for the modal's own pagination if needed
        // setModalTotalPages(Math.ceil(paginationInfo.total / itemsPerPage));
      }

    } catch (err) {
      setError(`Failed to fetch authorized detections: ${err.message}`);
    } finally {
      setLoading(false); // setLoadingModal(false)
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">securitybnb</h1>
                <p className="text-xs font-medium text-gray-500 tracking-wide">CCTV Monitoring</p>
              </div>
            </div>

            {/* Search Bar with Navigation */}
            {isTokenValid && (
              <div className="hidden lg:flex items-center animate-fadeIn">
                <div className="relative" id="search-bar-container">
                  <div 
                    className={`flex items-center bg-white/90 backdrop-blur-lg border border-gray-200/80 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 ${showSearchBar ? 'w-[600px]' : 'w-[480px]'}`}
                    onClick={() => setShowSearchBar(true)}
                  >
                    {!showSearchBar ? (
                      <div className="flex items-center w-full cursor-pointer">
                        {/* View Mode with Sliding Background */}
                        <div className="flex items-center px-4 py-2 border-r border-gray-300 relative">
                          <div 
                            className="absolute inset-y-1 bg-gray-100 rounded-lg transition-all duration-300 ease-out"
                            style={{
                              left: viewMode === 'cards' ? '18px' : '87px',
                              width: '60px'
                            }}
                          ></div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewMode('cards');
                            }}
                            className="relative z-10 px-3 py-1 text-sm font-semibold tracking-tight rounded-lg transition-colors mr-2"
                          >
                            Cards
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewMode('table');
                            }}
                            className="relative z-10 px-3 py-1 text-sm font-semibold tracking-tight rounded-lg transition-colors"
                          >
                            Table
                          </button>
                        </div>
                        
                        {/* Device Info */}
                        <div className="flex-1 px-4 py-3 border-r border-gray-300">
                          <span className="text-sm font-semibold tracking-tight text-gray-900">
                            {activeDeviceFilter ? `Device: ${activeDeviceFilter}` : 'All Devices'}
                          </span>
                          <span className="text-xs font-medium text-gray-500 tracking-wide ml-2">
                            {totalItems} alerts
                          </span>
                        </div>
                        
                        {/* Auto-refresh Status */}
                        <div className="flex items-center px-4 py-3 border-r border-gray-300">
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                            <span className="text-xs text-gray-600">
                              {autoRefresh ? `${refreshInterval}m` : 'Off'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Search Icon */}
                        <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3 ml-2">
                          <Search className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center w-full">
                        {/* Compact View Buttons */}
                        <div className="flex items-center px-3 py-3 border-r border-gray-300 relative">
                          <div 
                            className="absolute inset-y-1 bg-gray-100 rounded transition-all duration-300 ease-out"
                            style={{
                              left: viewMode === 'cards' ? '18px' : '70px',
                              width: '40px'
                            }}
                          ></div>
                          <button
                            onClick={() => setViewMode('cards')}
                            className="relative z-10 px-2 py-1 text-xs font-medium rounded transition-colors mr-1"
                          >
                            Cards
                          </button>
                          <button
                            onClick={() => setViewMode('table')}
                            className="relative z-10 px-2 py-1 text-xs font-medium rounded transition-colors"
                          >
                            Table
                          </button>
                        </div>
                        
                        {/* Search Input */}
                        <div className="flex-1 px-4 py-3 border-r border-gray-300">
                          <label className="text-xs font-semibold text-gray-900 block">Device</label>
                          <input
                            type="text"
                            placeholder="Search device..."
                            value={searchDevice}
                            onChange={(e) => setSearchDevice(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleDeviceSearch()}
                            className="w-full text-sm text-gray-700 placeholder-gray-400 border-none outline-none bg-transparent"
                            autoFocus
                          />
                        </div>
                        
                        {/* Auto-refresh Controls */}
                        <div className="px-3 py-3 border-r border-gray-300">
                          <label className="text-xs font-semibold text-gray-900 block mb-1">Auto-refresh</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={autoRefresh}
                              onChange={(e) => setAutoRefresh(e.target.checked)}
                              className="w-3 h-3 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-1"
                            />
                            <select
                              value={refreshInterval}
                              onChange={(e) => setRefreshInterval(Number(e.target.value))}
                              disabled={!autoRefresh}
                              className="text-xs px-1 py-0.5 border border-gray-300 rounded focus:ring-1 focus:ring-red-500 focus:border-red-500 disabled:opacity-50"
                            >
                              <option value={0.5}>30s</option>
                              <option value={1}>1m</option>
                              <option value={2}>2m</option>
                              <option value={5}>5m</option>
                              <option value={10}>10m</option>
                            </select>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center px-4">
                          <button
                            onClick={handleDeviceSearch}
                            disabled={!searchDevice.trim()}
                            className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center disabled:opacity-50 hover:bg-red-600 transition-colors"
                          >
                            <Search className="w-4 h-4 text-white" />
                          </button>
                          <button
                            onClick={() => setShowSearchBar(false)}
                            className="ml-2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              {isTokenValid ? (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      setShowAuthorizedSearchModal(true);
                      fetchAuthorizedAlerts(1);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-teal-50 text-teal-600 rounded-xl hover:bg-teal-100 transition-colors"
                  >
                    <Search className="w-4 h-4" />
                    <span className="text-sm font-semibold tracking-tight">Find Authorized</span>
                  </button>
                  
                  {/* Online Status Indicator */}
                  <div className="flex items-center space-x-2 text-sm font-semibold tracking-tight text-gray-600 leading-5">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Online</span>
                  </div>

                  {/* New Consolidated Menu */}
                  <div className="relative" id="user-menu-container">
                    {/* Menu Trigger Button */}
                    <button
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                      className="flex items-center space-x-2 p-2 border border-gray-300 rounded-full hover:shadow-md transition-shadow"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-gray-600">
                          <path fillRule="evenodd" d="M3 6.75A.75.75 0 013.75 6h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 6.75zM3 12a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 12zm0 5.25a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75a.75.75 0 01-.75-.75z" clipRule="evenodd" />
                      </svg>
                      <div className="w-6 h-6 bg-gradient-to-r from-red-500 to-pink-600 rounded-full flex items-center justify-center">
                          <Users className="w-4 h-4 text-white" />
                      </div>
                    </button>

                    {/* Dropdown Panel */}
                    {isMenuOpen && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 animate-fadeIn">
                        {/* Menu Items */}
                        <button onClick={() => { setShowScheduleModal(true); fetchSchedules(); setIsMenuOpen(false); }} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-3">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span>Schedules</span>
                        </button>
                        <button onClick={() => { setShowEventLog(true); fetchEventLogs(1); setIsMenuOpen(false); }} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-3">
                          <Activity className="w-4 h-4 text-gray-500" />
                          <span>Event Log</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowFaceRegistryModal(true);
                            fetchRegisteredPersons();
                            setIsMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-3"
                        >
                          <Users className="w-4 h-4 text-gray-500" />
                          <span>Face Registry</span>
                        </button>

                        {/* Divider */}
                        <div className="border-t border-gray-100 my-2"></div>
                        
                        {/* Sign Out */}
                        <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-100">
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-gray-500" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Controls */}
        {isTokenValid && (
          <div className="lg:hidden px-4 pb-4 animate-fadeIn">
            {/* View Mode Toggle */}
            <div className="flex items-center space-x-1 bg-gray-100 rounded-xl p-1 mb-3 relative">
              <div 
                className="absolute top-1 bottom-1 bg-white rounded-lg shadow-sm transition-all duration-300 ease-out w-1/2"
                style={{
                  left: viewMode === 'cards' ? '4px' : '50%'
                }}
              ></div>
              <button
                onClick={() => setViewMode('cards')}
                className="relative z-10 flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200"
              >
                Card View
              </button>
              <button
                onClick={() => setViewMode('table')}
                className="relative z-10 flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200"
              >
                Audit Log
              </button>
            </div>
            
            {/* Auto-refresh Controls */}
            <div className="bg-gray-50 border border-gray-300 rounded-xl p-3 mb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <span className="text-sm font-semibold tracking-tight text-gray-700">Auto-refresh</span>
                  <span className="text-xs font-medium text-gray-500 tracking-wide">
                    {autoRefresh ? `${refreshInterval}m` : 'Off'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                  />
                  <select
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(Number(e.target.value))}
                    disabled={!autoRefresh}
                    className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50"
                  >
                    <option value={0.5}>30s</option>
                    <option value={1}>1m</option>
                    <option value={2}>2m</option>
                    <option value={5}>5m</option>
                    <option value={10}>10m</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Search Bar */}
            <div 
              className="flex items-center bg-white/90 backdrop-blur-lg border border-gray-200/80 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200"
              onClick={() => setShowSearchBar(!showSearchBar)}
            >
              <Search className="w-5 h-5 text-gray-400 mr-3" />
              <span className="text-gray-600">
                {activeDeviceFilter ? `Device: ${activeDeviceFilter}` : 'Search devices...'}
              </span>
            </div>
            
            {showSearchBar && (
              <div className="mt-3 flex space-x-2 animate-fadeIn">
                <input
                  type="text"
                  placeholder="Enter device name"
                  value={searchDevice}
                  onChange={(e) => setSearchDevice(e.target.value)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                />
                <button
                  onClick={handleDeviceSearch}
                  className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                >
                  Search
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Login Section */}
      {!isTokenValid && (
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 tracking-tight mb-2">Welcome to SecurityBnB</h2>
              <p className="text-gray-600">Sign in with your credentials to start monitoring</p>
            </div>
            
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-lg"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-lg"
              />
              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white py-4 rounded-xl font-semibold hover:from-red-600 hover:to-pink-700 transition-all duration-200 disabled:opacity-50"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 mx-auto animate-spin" />
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
            
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      {isTokenValid && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5">
                    {showDetectedOnly ? 'Detected Alerts' : 'Total Alerts'}
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {showDetectedOnly ? detectedStats.total : stats.total}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${showDetectedOnly ? 'bg-purple-100' : 'bg-blue-100'
                  }`}>
                  {showDetectedOnly ? (
                    <Users className="w-6 h-6 text-purple-600" />
                  ) : (
                    <Activity className="w-6 h-6 text-blue-600" />
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5">Today</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {showDetectedOnly ? detectedStats.today : stats.today}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5">This Week</p>
                  <p className="text-3xl font-bold text-green-600">
                    {showDetectedOnly ? detectedStats.thisWeek : stats.thisWeek}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Active Filter */}
          {activeDeviceFilter && (
            <div className="mb-6">
              <div className="inline-flex items-center space-x-3 bg-blue-50 border border-blue-200 rounded-full px-4 py-2">
                <Filter className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold tracking-tight text-blue-900">
                  Showing alerts for: {activeDeviceFilter}
                </span>
                <button
                  onClick={handleShowAllDevices}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Detection Filter Indicator */}
          {showDetectedOnly && (
            <div className="mb-6">
              <div className="inline-flex items-center space-x-3 bg-purple-50 border border-purple-200 rounded-full px-4 py-2">
                <Users className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-semibold tracking-tight text-purple-900">
                  Showing only alerts with person detections
                </span>
                <button
                  onClick={() => {
                    setShowDetectedOnly(false);
                    setCurrentPage(1);
                    if (activeDeviceFilter) {
                      fetchDeviceAlerts(activeDeviceFilter, 1);
                    } else {
                      fetchAlerts(1);
                    }
                  }}
                  className="text-purple-600 hover:text-purple-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Device Quick Access */}
          {Array.isArray(availableDevices) && availableDevices.length > 0 && !activeDeviceFilter && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-4">Quick Device Access</h3>
              <div className="flex space-x-4 overflow-x-auto pb-2">
                {availableDevices.map(device => (
                  <button
                    key={device.device_sn}
                    onClick={() => {
                      setSearchDevice(device.device_sn);
                      setActiveDeviceFilter(device.device_sn);
                      setCurrentPage(1);
                      fetchDeviceAlerts(device.device_sn, 1);
                    }}
                    className="flex-shrink-0 px-8 py-6 bg-white border border-gray-300 rounded-2xl hover:border-gray-400 hover:shadow-lg hover:-translate-y-0.2 transition-all duration-200 min-w-[200px]"
                  >
                    <div className="flex items-start space-x-3">
                      <Camera className="w-5 h-5 text-gray-600 mt-0.5" />
                      <div className="text-left">
                        <span className="block text-sm font-semibold text-gray-900">{device.device_name || 'Unknown Device'}</span>
                        <span className="block text-xs font-medium text-gray-500 tracking-wide mt-1">{device.device_sn || 'N/A'}</span>
                        <span className="block text-xs text-gray-400 mt-1">{device.address || 'No address'}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* // Detection Filter Toggles */}
          {Array.isArray(availableDevices) && availableDevices.length > 0 && !activeDeviceFilter && (
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Alert Filter</h3>
                <button
                  onClick={() => {
                    setShowDetectedOnly(!showDetectedOnly);
                    setCurrentPage(1);
                    if (activeDeviceFilter) {
                      if (!showDetectedOnly) {
                        fetchDeviceDetectedAlerts(activeDeviceFilter, 1);
                      } else {
                        fetchDeviceAlerts(activeDeviceFilter, 1);
                      }
                    } else {
                      if (!showDetectedOnly) {
                        fetchDetectedAlerts(1);
                      } else {
                        fetchAlerts(1);
                      }
                    }
                  }}
                  className={`flex items-center space-x-3 px-6 py-3 rounded-2xl border transition-all duration-200 ${showDetectedOnly
                      ? 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <Users className="w-5 h-5" />
                  <span className="font-medium">
                    {showDetectedOnly ? 'Showing: Detected Only' : 'Showing: All Alerts'}
                  </span>
                  <div className={`w-12 h-6 rounded-full transition-colors relative ${showDetectedOnly ? 'bg-purple-500' : 'bg-gray-300'
                    }`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform absolute top-0.5 ${showDetectedOnly ? 'translate-x-6' : 'translate-x-0.5'
                      }`}></div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Alternative placement when device filter is active */}
          {activeDeviceFilter && (
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Alert Filter</h3>
                {/* Keep only the Detected Filter Toggle */}
                <button
                  onClick={() => {
                    setShowDetectedOnly(!showDetectedOnly);
                    setCurrentPage(1);
                  }}
                  className={`flex items-center space-x-3 px-6 py-3 rounded-2xl border transition-all duration-200 ${showDetectedOnly
                    ? 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                    : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <Users className="w-5 h-5" />
                  <span className="font-medium">
                    {showDetectedOnly ? 'Showing: Detected Only' : 'Showing: All Alerts'}
                  </span>
                  <div className={`w-12 h-6 rounded-full transition-colors relative ${showDetectedOnly ? 'bg-purple-500' : 'bg-gray-300'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform absolute top-0.5 ${showDetectedOnly ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Content with Smooth Transitions */}
          {alerts.length > 0 && (
            <div className="transition-all duration-500 ease-out">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">
                  {viewMode === 'cards' 
                    ? (activeDeviceFilter ? `Alerts from ${activeDeviceFilter}` : 'Security Alerts')
                    : (activeDeviceFilter ? `Audit Log - ${activeDeviceFilter}` : 'Security Audit Log')
                  }
                </h2>
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-semibold tracking-tight text-gray-600 leading-5">
                    Page {currentPage} of {totalPages} • {totalItems} total alerts
                  </span>
                  <button
                    onClick={() => {
                      if (activeDeviceFilter) {
                        if (showDetectedOnly) {
                          fetchDeviceDetectedAlerts(activeDeviceFilter, currentPage);
                        } else {
                          fetchDeviceAlerts(activeDeviceFilter, currentPage);
                        }
                      } else {
                        if (showDetectedOnly) {
                          fetchDetectedAlerts(currentPage);
                        } else {
                          fetchAlerts(currentPage);
                        }
                      }
                    }}
                    disabled={loading}
                    className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {viewMode === 'cards' ? (
                /* Card View */
                <div className="animate-slideInLeft">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {alerts.map((alert, index) => (
                      <div
                        key={alert.id}
                        className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-2xl hover:scale-[1.02] hover:border-gray-300 transition-all duration-300 cursor-pointer animate-fadeInStagger"
                        style={{ animationDelay: `${index * 50}ms` }}
                        onClick={() => setSelectedAlert(alert)}
                      >
                        <div className="aspect-square bg-gray-100 relative overflow-hidden">
                          <img
                            src={getImageUrl(alert.image_path)}
                            alt={`Alert from ${alert.camera_name}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden absolute inset-0 items-center justify-center bg-gray-100">
                            <Camera className="w-12 h-12 text-gray-400" />
                          </div>
                          
                          <div className="absolute top-3 left-3">
                            <span className="inline-flex items-center px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-full shadow-sm border border-red-400">
                              ALERT
                            </span>
                          </div>
                          
                          <div className="absolute top-3 right-3">
                            <span className="px-2 py-1 bg-black/70 text-white text-xs rounded-lg backdrop-blur-sm">
                              {getTimeAgo(alert.timestamp)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-semibold text-gray-900 tracking-tight group-hover:text-red-600 transition-colors">
                              {alert.camera_name}
                            </h3>
                            <span className="text-xs font-medium text-gray-500 tracking-wide bg-gray-100 px-2 py-1 rounded-full">
                              #{alert.id}
                            </span>
                          </div>
                          
                          <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5 mb-3">{alert.device_sn}</p>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-xs font-medium text-gray-500 tracking-wide">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatTimestamp(alert.timestamp)}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowBoundingBoxes(alert);
                              }}
                              className="flex items-center text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                            >
                              <Users className="w-3 h-3 mr-1" />
                              Detect People
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Table View */
                <div className="animate-slideInRight">
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Alert ID
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Camera Name
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Device Serial
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Detection Type
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Timestamp
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {alerts.map((alert, index) => (
                            <tr 
                              key={alert.id} 
                              className="hover:bg-gray-50 transition-colors animate-fadeInStagger"
                              style={{ animationDelay: `${index * 30}ms` }}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold tracking-tight text-gray-900">#{alert.id}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{alert.camera_name}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-700">{alert.device_sn}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                                  Motion Detection
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-700">{formatTimestamp(alert.timestamp)}</div>
                                <div className="text-xs font-medium text-gray-500 tracking-wide">{getTimeAgo(alert.timestamp)}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1"></div>
                                  Active
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold tracking-tight">
                                <button
                                  onClick={() => setSelectedAlert(alert)}
                                  className="text-red-600 hover:text-red-900 mr-4 transition-colors"
                                >
                                  View Details
                                </button>
                                <button
                                  onClick={() => handleShowBoundingBoxes(alert)}
                                  className="text-blue-600 hover:text-blue-900 mr-4 transition-colors inline-flex items-center"
                                >
                                  <Users className="w-4 h-4 mr-1" />
                                  Detect People
                                </button>
                                <a
                                  href={getImageUrl(alert.image_path)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                  View Image
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Enhanced Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-12 space-y-4 sm:space-y-0">
                  {/* Pagination Info */}
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, totalItems)}
                    </span> of{' '}
                    <span className="font-medium">{totalItems}</span> results
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center space-x-2">
                    {/* First Page */}
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1 || loading}
                      className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span className="sr-only">First page</span>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>

                    {/* Previous Page */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {getPaginationRange().map(page => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                          className={`w-10 h-10 rounded-lg transition-all ${
                            page === currentPage
                              ? 'bg-red-500 text-white shadow-lg'
                              : 'border border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    {/* Next Page */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || loading}
                      className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>

                    {/* Last Page */}
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages || loading}
                      className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span className="sr-only">Last page</span>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414zm6 0a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L14.586 10l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <RefreshCw className="w-8 h-8 text-white animate-spin" />
                </div>
                <p className="text-lg font-medium text-gray-600 tracking-tight">Loading alerts...</p>
                <div className="mt-4 flex justify-center space-x-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse animation-delay-100"></div>
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse animation-delay-200"></div>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Alerts</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => {
                  if (activeDeviceFilter) {
                    if (showDetectedOnly) {
                      fetchDeviceDetectedAlerts(activeDeviceFilter, currentPage);
                    } else {
                      fetchDeviceAlerts(activeDeviceFilter, currentPage);
                    }
                  } else {
                    if (showDetectedOnly) {
                      fetchDetectedAlerts(currentPage);
                    } else {
                      fetchAlerts(currentPage);
                    }
                  }
                }}
                className="bg-red-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-red-600 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {alerts.length === 0 && !loading && !error && (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gradient-to-r from-red-500 to-pink-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <Camera className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 tracking-tight mb-3">No alerts yet</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                {activeDeviceFilter 
                  ? `No security alerts found for device ${activeDeviceFilter}.`
                  : 'Your security system is all clear. No alerts to display at the moment.'
                }
              </p>
              {activeDeviceFilter && (
                <button
                  onClick={handleShowAllDevices}
                  className="bg-red-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-red-600 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                >
                  View All Devices
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bounding Box Modal */}
      {boundingBoxModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Person Identification</h2>
                  <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5">Alert #{boundingBoxModal.id} • {boundingBoxModal.camera_name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setBoundingBoxModal(null);
                  setBoundingBoxData(null);

                  if (reopenSearchOnClose) {
                    setShowAuthorizedSearchModal(true);
                    setReopenSearchOnClose(false);
                  }
                }}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-8">
              {loadingBoundingBox ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Scan className="w-8 h-8 text-white animate-pulse" />
                    </div>
                    <p className="text-lg text-gray-600">Analyzing image for person identification...</p>
                    <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
                  </div>
                </div>
              ) : boundingBoxData ? (
                  boundingBoxData.results.length > 0 ? (
                    <div className="space-y-8">
                      {/* Image with Bounding Boxes */}
                      <div className="relative bg-gray-100 rounded-2xl overflow-hidden">
                        <img
                          id="bounding-box-image"
                          src={getImageUrl(boundingBoxModal.image_path)}
                          alt={`Alert from ${boundingBoxModal.camera_name}`}
                          className="w-full h-auto object-contain"
                          onLoad={(e) => {
                            // Trigger re-render to show bounding boxes after image loads
                            setTimeout(() => {
                              setBoundingBoxData({ ...boundingBoxData });
                            }, 100);
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden h-96 items-center justify-center">
                          <div className="text-center">
                            <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500">Image not available</p>
                          </div>
                        </div>

                        {/* Bounding Box Overlay */}
                        <BoundingBoxOverlay
                          boundingBoxData={boundingBoxData}
                          imageElement={document.getElementById('bounding-box-image')}
                        />
                      </div>

                      {/* Detection Summary */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="text-sm font-semibold text-green-800">Authorized</span>
                          </div>
                          <p className="text-2xl font-bold text-green-900">
                            {boundingBoxData.results.filter(r => r.authorized).length}
                          </p>
                        </div>

                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <span className="text-sm font-semibold text-red-800">Unauthorized</span>
                          </div>
                          <p className="text-2xl font-bold text-red-900">
                            {boundingBoxData.results.filter(r => !r.authorized).length}
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Users className="w-4 h-4 text-gray-600" />
                            <span className="text-sm font-semibold text-gray-800">Total People</span>
                          </div>
                          <p className="text-2xl font-semibold text-gray-900 tracking-tight">
                            {boundingBoxData.results.length}
                          </p>
                        </div>
                      </div>

                      {/* Person Details */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-4">Detected Persons</h3>
                        <div className="space-y-3">
                          {boundingBoxData.results.map((result, index) => (
                            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                              <div className="flex items-center space-x-3">
                                <div className={`w-4 h-4 rounded-full ${result.authorized ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <div>
                                  <p className="font-medium text-gray-900">{result.person_id}</p>
                                  <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5">
                                    {result.authorized ? 'Authorized Access' : 'Unauthorized Access'}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                {result.confidence > 0 && (
                                  <p className="text-sm font-semibold tracking-tight text-gray-900">
                                    {Math.round(result.confidence * 100)}% confidence
                                  </p>
                                )}
                                <p className="text-xs font-medium text-gray-500 tracking-wide">
                                  Position: ({result.bounding_box.x1}, {result.bounding_box.y1})
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Alert Information */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">Alert Details</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-blue-700 font-medium">Camera:</span>
                            <span className="text-blue-900 ml-2">{boundingBoxModal.camera_name}</span>
                          </div>
                          <div>
                            <span className="text-blue-700 font-medium">Device:</span>
                            <span className="text-blue-900 ml-2">{boundingBoxModal.device_sn}</span>
                          </div>
                          <div>
                            <span className="text-blue-700 font-medium">Time:</span>
                            <span className="text-blue-900 ml-2">{formatTimestamp(boundingBoxModal.timestamp)}</span>
                          </div>
                          <div>
                            <span className="text-blue-700 font-medium">Status:</span>
                            <span className="text-blue-900 ml-2">{boundingBoxData.status}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* No Detection State */
                    <div className="text-center py-20">
                      <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                        <Users className="w-12 h-12 text-white" />
                      </div>
                      <h3 className="text-2xl font-semibold text-gray-900 tracking-tight mb-3">No People Detected</h3>
                      <p className="text-gray-600 mb-8 max-w-md mx-auto">
                        The AI analysis completed successfully, but no people were detected in this alert image. This could be due to motion from objects, lighting changes, or other environmental factors.
                      </p>

                      {/* Alert Information for No Detection */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left max-w-md mx-auto">
                        <h4 className="font-semibold text-blue-900 mb-2">Alert Details</h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-blue-700 font-medium">Camera:</span>
                            <span className="text-blue-900 ml-2">{boundingBoxModal.camera_name}</span>
                          </div>
                          <div>
                            <span className="text-blue-700 font-medium">Device:</span>
                            <span className="text-blue-900 ml-2">{boundingBoxModal.device_sn}</span>
                          </div>
                          <div>
                            <span className="text-blue-700 font-medium">Time:</span>
                            <span className="text-blue-900 ml-2">{formatTimestamp(boundingBoxModal.timestamp)}</span>
                          </div>
                          <div>
                            <span className="text-blue-700 font-medium">Analysis:</span>
                            <span className="text-blue-900 ml-2">Complete - No persons found</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Analysis Failed</h3>
                  <p className="text-gray-600">Unable to load person identification data for this alert.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Airbnb-Style Alert Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">About this alert</h2>
              <button
                onClick={() => setSelectedAlert(null)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-8">
              {/* Alert Image */}
              <div className="mb-6">
                <div className="rounded-2xl overflow-hidden shadow-sm bg-gray-100">
                  <img
                    src={getImageUrl(selectedAlert.image_path)}
                    alt={`Alert from ${selectedAlert.camera_name}`}
                    className="w-full h-80 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden h-80 items-center justify-center">
                    <div className="text-center">
                      <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">Image not available</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alert Information */}
              <div className="space-y-8">
                {/* Main Description */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{selectedAlert.camera_name}</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Security alert triggered on {formatTimestamp(selectedAlert.timestamp)}. This alert was captured by the {selectedAlert.camera_name} camera system and requires immediate attention from security personnel. The system has automatically flagged this event based on configured detection parameters for potential security threats or unusual activity.
                  </p>
                </div>

                {/* Alert Access */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 tracking-tight mb-4">Alert access</h4>
                  <div className="text-gray-700 space-y-1">
                    <div>• Alert ID: #{selectedAlert.id}</div>
                    <div>• Device Serial: {selectedAlert.device_sn}</div>
                    <div>• Camera Name: {selectedAlert.camera_name}</div>
                    <div>• Detection Time: {formatTimestamp(selectedAlert.timestamp)}</div>
                    <div>• Response Priority: <span className="text-red-600 font-medium">High Alert</span></div>
                    <div>• Time Since Detection: {getTimeAgo(selectedAlert.timestamp)}</div>
                  </div>
                </div>

                {/* System Capabilities */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 tracking-tight mb-4">System capabilities</h4>
                  <p className="text-gray-700 mb-3">
                    Furthermore, this security monitoring system has advanced capabilities which security teams can utilize during incident response:
                  </p>
                  <div className="text-gray-700 space-y-1">
                    <div>• Real-time CCTV monitoring and surveillance</div>
                    <div>• Automated motion and intrusion detection</div>
                    <div>• 24/7 continuous security monitoring</div>
                    <div>• High-resolution image capture (1080p+)</div>
                    <div>• Intelligent threat classification system</div>
                    <div>• Remote access and mobile notifications</div>
                    <div>• Comprehensive incident logging and reporting</div>
                    <div>• Multi-camera coordination and tracking</div>
                  </div>
                </div>

                {/* Other Things to Note */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 tracking-tight mb-4">Other things to note</h4>
                  <div className="text-gray-700 space-y-1">
                    <div>• Alert verification is strongly recommended before escalation</div>
                    <div>• Security personnel should be notified immediately for high-priority alerts</div>
                    <div>• Original image available at: <a href={getImageUrl(selectedAlert.image_path)} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 underline">View full resolution</a></div>
                    <div>• System logs are automatically archived for forensic analysis</div>
                    <div>• Follow standard operating procedures for this alert classification</div>
                    <div>• Document all investigative findings in the incident management system</div>
                    <div>• Coordinate with on-site security personnel if immediate response is required</div>
                    <div>• Consider reviewing neighboring camera feeds for additional context</div>
                    <div>• Archive this alert record after completing proper investigation protocols</div>
                    <div>• Image path reference: <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{selectedAlert.image_path}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Event Log Modal */}
      {showEventLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">System Event Log</h2>
                  <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5">Camera and device status events</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowEventLog(false);
                  setEventLogs([]);
                  setEventCurrentPage(1);
                }}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-8">
              {loadingEvents ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <RefreshCw className="w-8 h-8 text-white animate-spin" />
                    </div>
                    <p className="text-lg text-gray-600">Loading event logs...</p>
                  </div>
                </div>
              ) : eventError ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Event Log</h3>
                  <p className="text-gray-600 mb-6">{eventError}</p>
                  <button
                    onClick={() => fetchEventLogs(eventCurrentPage)}
                    className="bg-blue-500 text-white px-6 py-3 rounded-xl hover:bg-blue-600 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : eventLogs.length > 0 ? (
                <div className="space-y-8">
                  {/* Summary */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
                      Event History
                    </h3>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-semibold tracking-tight text-gray-600 leading-5">
                        Page {eventCurrentPage} of {eventTotalPages} • {eventTotalItems} total events
                      </span>
                      <button
                        onClick={() => fetchEventLogs(eventCurrentPage)}
                        disabled={loadingEvents}
                        className={`p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-all duration-200 ${loading ? 'animate-pulse' : 'hover:scale-110'}`}
                      >
                        <RefreshCw className={`w-4 h-4 ${loadingEvents ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>

                  {/* Event List */}
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Event ID
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Camera
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Device
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Event Type
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Message
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Timestamp
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {eventLogs.map((event, index) => (
                            <tr
                              key={event.id}
                              className="hover:bg-gray-50 transition-colors animate-fadeInStagger"
                              style={{ animationDelay: `${index * 30}ms` }}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold tracking-tight text-gray-900">#{event.id}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{event.camera_name}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-700">{event.device_sn}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getEventTypeColor(event.event_type)}`}>
                                  {event.event_type}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-700 max-w-md">{event.message}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-700">{formatTimestamp(event.timestamp)}</div>
                                <div className="text-xs font-medium text-gray-500 tracking-wide">{getTimeAgo(event.timestamp)}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Event Pagination */}
                  {eventTotalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      {/* Pagination Info */}
                      <div className="text-sm text-gray-700">
                        Showing <span className="font-medium">{((eventCurrentPage - 1) * eventItemsPerPage) + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(eventCurrentPage * eventItemsPerPage, eventTotalItems)}
                        </span> of{' '}
                        <span className="font-medium">{eventTotalItems}</span> events
                      </div>

                      {/* Pagination Controls */}
                      <div className="flex items-center space-x-2">
                        {/* Previous Page */}
                        <button
                          onClick={() => handleEventPageChange(eventCurrentPage - 1)}
                          disabled={eventCurrentPage === 1 || loadingEvents}
                          className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {getEventPaginationRange().map(page => (
                            <button
                              key={page}
                              onClick={() => handleEventPageChange(page)}
                              disabled={loadingEvents}
                              className={`w-10 h-10 rounded-lg transition-all ${page === eventCurrentPage
                                  ? 'bg-blue-500 text-white shadow-lg'
                                  : 'border border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                                }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>

                        {/* Next Page */}
                        <button
                          onClick={() => handleEventPageChange(eventCurrentPage + 1)}
                          disabled={eventCurrentPage === eventTotalPages || loadingEvents}
                          className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Activity className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Events Found</h3>
                  <p className="text-gray-600">No system events have been recorded yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Detection Schedules</h2>
                  <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5">Manage when cameras detect motion and recognize people</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setEditingSchedule(null);
                  setScheduleForm({
                    device_sn: '',
                    name: '',
                    start_time: '',
                    end_time: '',
                    days: [],
                    motion_detection: true,
                    person_recognition: true,
                    is_active: true
                  });
                }}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-green-800">Active Schedules</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900">
                    {schedules.filter(s => s.is_active).length}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-blue-800">Currently Running</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {getCurrentActiveSchedules().length}
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-semibold text-gray-800">Total Schedules</span>
                  </div>
                  <p className="text-2xl font-semibold text-gray-900 tracking-tight">
                    {schedules.length}
                  </p>
                </div>
              </div>

              {/* Add New Schedule Button */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
                  Schedule List
                </h3>
                <button
                  onClick={() => {
                    setEditingSchedule(null);
                    setScheduleForm({
                      device_sn: '',
                      name: '',
                      start_time: '',
                      end_time: '',
                      days: [],
                      motion_detection: true,
                      person_recognition: true,
                      is_active: true
                    });
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="text-sm font-semibold tracking-tight">Add Schedule</span>
                </button>
              </div>

              {loadingSchedules ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <RefreshCw className="w-8 h-8 text-white animate-spin" />
                    </div>
                    <p className="text-lg text-gray-600">Loading schedules...</p>
                  </div>
                </div>
              ) : scheduleError ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Schedules</h3>
                  <p className="text-gray-600 mb-6">{scheduleError}</p>
                  <button
                    onClick={() => fetchSchedules()}
                    className="bg-green-500 text-white px-6 py-3 rounded-xl hover:bg-green-600 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : schedules.length > 0 ? (
                <div className="space-y-4">
                  {schedules.map((schedule, index) => (
                    <div key={schedule.id} className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-md transition-shadow animate-fadeInStagger" style={{ animationDelay: `${index * 50}ms` }}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900 tracking-tight">{schedule.name}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${schedule.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                              }`}>
                              {schedule.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {getCurrentActiveSchedules().some(s => s.id === schedule.id) && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 animate-pulse">
                                Running Now
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600 font-medium">Device:</span>
                              <div className="text-gray-900">{schedule.device_name}</div>
                              <div className="text-gray-500 text-xs">{schedule.device_sn}</div>
                            </div>
                            <div>
                              <span className="text-gray-600 font-medium">Time:</span>
                              <div className="text-gray-900">{schedule.start_time} - {schedule.end_time}</div>
                            </div>
                            <div>
                              <span className="text-gray-600 font-medium">Days:</span>
                              <div className="text-gray-900">{formatDays(schedule.days)}</div>
                            </div>
                            <div>
                              <span className="text-gray-600 font-medium">Detection:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {schedule.motion_detection && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Motion</span>
                                )}
                                {schedule.person_recognition && (
                                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Person</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          <button
                            onClick={() => handleToggleSchedule(schedule.id, !schedule.is_active)}
                            disabled={loadingSchedules}
                            className={`w-12 h-6 rounded-full transition-colors relative ${schedule.is_active ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                          >
                            <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform absolute top-0.5 ${schedule.is_active ? 'translate-x-6' : 'translate-x-0.5'
                              }`}></div>
                          </button>
                          <button
                            onClick={() => handleEditSchedule(schedule)}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeleteConfirmModal(schedule)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Schedules Found</h3>
                  <p className="text-gray-600 mb-6">Create your first detection schedule to automate camera monitoring.</p>
                  <button
                    onClick={() => {
                      setEditingSchedule(null);
                      setScheduleForm({
                        device_sn: '',
                        name: '',
                        start_time: '',
                        end_time: '',
                        days: [],
                        motion_detection: true,
                        person_recognition: true,
                        is_active: true
                      });
                    }}
                    className="bg-green-500 text-white px-6 py-3 rounded-xl hover:bg-green-600 transition-colors"
                  >
                    Create Schedule
                  </button>
                </div>
              )}

              {/* Schedule Form */}
              {(editingSchedule || scheduleForm.device_sn || scheduleForm.name) && (
                <div className="mt-8 bg-gray-50 rounded-2xl p-8">
                  <h4 className="text-lg font-semibold text-gray-900 tracking-tight mb-4">
                    {editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
                  </h4>

                  <form onSubmit={handleScheduleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold tracking-tight text-gray-700 mb-2">Schedule Name</label>
                        <input
                          type="text"
                          required
                          value={scheduleForm.name}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                          placeholder="e.g., Business Hours, Night Security"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold tracking-tight text-gray-700 mb-2">Camera Device</label>
                        <select
                          required
                          value={scheduleForm.device_sn}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, device_sn: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                        >
                          <option value="">Select a camera</option>
                          {availableDevices.map(device => (
                            <option key={device.device_sn} value={device.device_sn}>
                              {device.device_name || device.device_sn}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold tracking-tight text-gray-700 mb-2">Start Time</label>
                        <input
                          type="time"
                          required
                          value={scheduleForm.start_time}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold tracking-tight text-gray-700 mb-2">End Time</label>
                        <input
                          type="time"
                          required
                          value={scheduleForm.end_time}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold tracking-tight text-gray-700 mb-2">Days of Week</label>
                      <div className="grid grid-cols-7 gap-2">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                          <label key={day} className="flex flex-col items-center">
                            <input
                              type="checkbox"
                              checked={scheduleForm.days.includes(day)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setScheduleForm({ ...scheduleForm, days: [...scheduleForm.days, day] });
                                } else {
                                  setScheduleForm({ ...scheduleForm, days: scheduleForm.days.filter(d => d !== day) });
                                }
                              }}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                            />
                            <span className="text-xs text-gray-600 mt-1 capitalize">{day.slice(0, 3)}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold tracking-tight text-gray-700 mb-2">Detection Types</label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={scheduleForm.motion_detection}
                            onChange={(e) => setScheduleForm({ ...scheduleForm, motion_detection: e.target.checked })}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <span className="ml-2 text-sm text-gray-700">Motion Detection</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={scheduleForm.person_recognition}
                            onChange={(e) => setScheduleForm({ ...scheduleForm, person_recognition: e.target.checked })}
                            className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                          />
                          <span className="ml-2 text-sm text-gray-700">Person Recognition</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 pt-4">
                      <button
                        type="submit"
                        disabled={loadingSchedules}
                        className="bg-green-500 text-white px-6 py-3 rounded-xl hover:bg-green-600 transition-colors disabled:opacity-50"
                      >
                        {loadingSchedules ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          editingSchedule ? 'Update Schedule' : 'Create Schedule'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingSchedule(null);
                          setScheduleForm({
                            device_sn: '',
                            name: '',
                            start_time: '',
                            end_time: '',
                            days: [],
                            motion_detection: true,
                            person_recognition: true,
                            is_active: true
                          });
                        }}
                        className="bg-gray-100 text-gray-700 px-8 py-4 rounded-xl font-medium hover:bg-gray-200 hover:shadow-sm transition-all duration-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Authorized Alerts Search Modal*/}
      {showAuthorizedSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col animate-scaleIn">
            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Authorized Personnel Alerts</h2>
                  <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5">Showing all alerts with authorized detections</p>
                </div>
              </div>
              <button
                onClick={() => setShowAuthorizedSearchModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content & Results */}
            <div className="p-8 overflow-y-auto">
              {loading && (
                <div className="text-center py-20">
                    <RefreshCw className="w-8 h-8 mx-auto text-gray-400 animate-spin" />
                    <p className="mt-4 text-gray-600">Loading authorized alerts...</p>
                </div>
              )}

              {!loading && authorizedSearchResults.length === 0 && (
                 <div className="text-center py-20">
                    <Users className="w-12 h-12 mx-auto text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No Authorized Alerts Found</h3>
                    <p className="mt-1 text-sm text-gray-500">The system has not found any alerts with authorized personnel.</p>
                 </div>
              )}

              {!loading && authorizedSearchResults.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                  {authorizedSearchResults.map((alert) => (
                    <div
                      key={alert.alert_id} // Use the correct unique ID for the key
                      className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer"
                      onClick={() => {
                        // 1. Show the Person ID modal with the correct data
                        handleShowPreloadedDetections(alert);
                        // 2. Hide the search modal so we can focus on the details
                        setShowAuthorizedSearchModal(false);
                        // 3. Set our flag so we know where to return to
                        setReopenSearchOnClose(true);
                      }}
                    >
                      <div className="aspect-square bg-gray-100 relative">
                        <img
                          src={getImageUrl(alert.image_path)} // Access properties directly
                          alt={`Alert from ${alert.camera_name}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-3 right-3 px-2 py-1 bg-black/70 text-white text-xs rounded-lg">
                          {getTimeAgo(alert.timestamp)}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 group-hover:text-teal-600">
                          {alert.camera_name}
                        </h3>
                        <p className="text-sm text-gray-500">{alert.device_sn}</p>
                        <div className="text-xs text-gray-500 mt-2">{formatTimestamp(alert.timestamp)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer with Pagination */}
            {!loading && authorizedSearchResults.length > 0 && (
                <div className="p-4 border-t border-gray-100 mt-auto">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => fetchAuthorizedAlerts(searchPage - 1)}
                            disabled={searchPage <= 1}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-700">
                            Page <span className="font-medium">{searchPage}</span>
                        </span>
                        <button
                            onClick={() => fetchAuthorizedAlerts(searchPage + 1)}
                            // We can add a disabled state here if the API tells us we are on the last page
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* Face Registry Modal                                           */}
      {/* ==================================================================== */}
      {showFaceRegistryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col animate-scaleIn">
            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Face Registry</h2>
                  <p className="text-sm font-semibold tracking-tight text-gray-600 leading-5">
                    {registeredPersons.length} individuals registered in the system
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowFaceRegistryModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content & Results */}
            <div className="p-8 overflow-y-auto">
              {loadingRegistry ? (
                <div className="text-center py-20">
                  <RefreshCw className="w-8 h-8 mx-auto text-gray-400 animate-spin" />
                  <p className="mt-4 text-gray-600">Loading registry...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
                  {registeredPersons.map((person, index) => (
                    <div
                      key={person.person_id}
                      className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fadeInStagger"
                      style={{ animationDelay: `${index * 40}ms` }}
                    >
                      <div className="aspect-square bg-gray-50 relative">
                        {/* Placeholder Image */}
                        <img
                          src={`https://placehold.co/400x400/EFEFEF/AAAAAA?text=${person.person_id.charAt(0)}`}
                          alt={`Placeholder for ${person.person_id}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4 text-center">
                        <h3 className="font-semibold text-gray-900 group-hover:text-purple-600">
                          {person.person_id}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {person.image_count} images registered
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInStagger {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }
        
        .animate-slideInLeft {
          animation: slideInLeft 0.4s ease-out;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.4s ease-out;
        }
        
        .animate-fadeInStagger {
          animation: fadeInStagger 0.3s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default CCTVDashboard;